import React, { useState } from "react";
import { jsPDF } from "jspdf";
import { 
  Book, 
  LogIn, 
  MapPin, 
  ClipboardList, 
  Clock, 
  Users, 
  Settings, 
  Shield,
  ChevronDown,
  ChevronRight,
  Home,
  Map,
  FileText,
  UserCog,
  Building,
  HelpCircle,
  CheckCircle,
  AlertTriangle,
  Download,
  PlayCircle,
  Video
} from "lucide-react";

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-orange-500" />
          <span className="font-semibold text-lg">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-zinc-500" />
        ) : (
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white dark:bg-zinc-900">
          {children}
        </div>
      )}
    </div>
  );
};

const Step = ({ number, title, children }) => (
  <div className="flex gap-4 mb-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-zinc-600 dark:text-zinc-400 text-sm">{children}</p>
    </div>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
    <HelpCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-blue-800 dark:text-blue-200">{children}</p>
  </div>
);

const Warning = ({ children }) => (
  <div className="flex gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-800 dark:text-amber-200">{children}</p>
  </div>
);

const VideoTutorial = ({ title, description, videoId, duration }) => (
  <div className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden mb-4">
    <div className="aspect-video bg-zinc-900 relative group cursor-pointer">
      {/* Video Thumbnail/Placeholder */}
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-orange-500/90 flex items-center justify-center mx-auto mb-3 group-hover:bg-orange-500 transition-colors group-hover:scale-110 transform duration-200">
            <PlayCircle className="w-10 h-10 text-white" />
          </div>
          <p className="text-zinc-400 text-sm">{duration}</p>
        </div>
      </div>
      {/* If you have actual video URLs, replace with: */}
      {/* <iframe 
        src={`https://www.youtube.com/embed/${videoId}`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      /> */}
    </div>
    <div className="p-4 bg-white dark:bg-zinc-800">
      <h4 className="font-semibold mb-1">{title}</h4>
      <p className="text-sm text-zinc-600 dark:text-zinc-400">{description}</p>
    </div>
  </div>
);

// PDF Generation Function
const generatePDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let yPos = 20;
  
  // Helper function to add text with word wrap
  const addText = (text, fontSize = 12, isBold = false) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", isBold ? "bold" : "normal");
    const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);
    
    lines.forEach(line => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += fontSize * 0.5;
    });
    yPos += 5;
  };
  
  const addSection = (title) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    yPos += 5;
    doc.setFillColor(255, 107, 0);
    doc.rect(margin, yPos - 5, 3, 12, 'F');
    addText(title, 14, true);
    yPos += 3;
  };
  
  // Title
  doc.setFillColor(39, 39, 42);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("Station Logbook", margin, 25);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text("User Manual & Guide", margin, 33);
  
  doc.setTextColor(0, 0, 0);
  yPos = 55;
  
  // Quick Start
  addSection("Quick Start Guide");
  addText("Get started with Station Logbook in just a few steps:");
  addText("1. Log In - Enter your Employee ID and password on the login page.");
  addText("2. View Dashboard - See all available stations after logging in.");
  addText("3. Start a Station Visit - Click 'New Entry' or select a station.");
  addText("4. Complete Your Log - Fill in all required information.");
  
  // Login & Authentication
  addSection("Login & Authentication");
  addText("Standard Login: Use your assigned Employee ID and password.");
  addText("SSO: If enabled, use GridGate SSO for corporate credentials.");
  addText("Registration: New users can register via the Register tab.");
  
  // Dashboard
  addSection("Dashboard");
  addText("The Dashboard provides an overview of all stations:");
  addText("• Station List: View all substations with details");
  addText("• Search: Find stations by name, ID, or location");
  addText("• Quick Actions: Start entries directly from station cards");
  
  // Station Entry
  addSection("Creating a Station Entry");
  addText("1. Select a Station from Dashboard or Entry page");
  addText("2. Record Arrival Time (auto-recorded, adjustable)");
  addText("3. Document Activities performed during visit");
  addText("4. Log Equipment Status and readings");
  addText("5. Complete the Entry to finalize");
  
  // Map View
  addSection("Station Map View");
  addText("Interactive map features:");
  addText("• Marker Clustering for better visibility");
  addText("• Heatmap View for station density");
  addText("• Region and Office Filters");
  addText("• Search for specific stations");
  
  // History
  addSection("Visit History");
  addText("Access complete history of station visits:");
  addText("• Filter by date range, station, or status");
  addText("• Click entries to view full details");
  addText("• Export history to PDF for reports");
  
  // User Roles
  addSection("User Roles & Permissions");
  addText("Worker: Create/edit own entries, view stations and map");
  addText("Supervisor: Review team entries, access statistics");
  addText("Administrator: Full access, manage users and stations");
  
  // Troubleshooting
  addSection("Troubleshooting");
  addText("Can't log in? Verify credentials or contact administrator.");
  addText("Station not found? Try searching by station code.");
  addText("Entry not saving? Check internet and required fields.");
  addText("Map not loading? Refresh page or clear browser cache.");
  
  // Footer
  doc.addPage();
  yPos = 20;
  addText("Station Logbook v1.0", 10);
  addText("Digital Substation Log Book", 10);
  addText("For support, contact your system administrator.", 10);
  
  // Save
  doc.save("Station_Logbook_User_Manual.pdf");
};

export default function UserManualPage() {
  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-900 to-zinc-800 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center">
                <Book className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Station Logbook</h1>
                <p className="text-zinc-400">User Manual & Guide</p>
              </div>
            </div>
            <button
              onClick={generatePDF}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors font-medium"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
          </div>
          <p className="text-zinc-300 max-w-2xl mt-4">
            Welcome to the Station Logbook digital platform. This guide will help you understand 
            how to use all features of the application effectively.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Video Tutorials Section */}
        <Section title="Video Tutorials" icon={Video} defaultOpen={false}>
          <p className="mb-4 text-zinc-600 dark:text-zinc-400">
            Watch these video tutorials to learn how to use Station Logbook effectively.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <VideoTutorial 
              title="Getting Started"
              description="Learn the basics of logging in, navigating the dashboard, and creating your first station entry."
              videoId="getting-started"
              duration="3:45"
            />
            <VideoTutorial 
              title="Creating Station Entries"
              description="Step-by-step guide to documenting station visits, logging activities, and recording equipment status."
              videoId="station-entries"
              duration="5:20"
            />
            <VideoTutorial 
              title="Using the Map View"
              description="Navigate the interactive station map, use filters, and understand the heatmap visualization."
              videoId="map-view"
              duration="4:15"
            />
            <VideoTutorial 
              title="Supervisor Features"
              description="For supervisors: reviewing team entries, managing approvals, and viewing team statistics."
              videoId="supervisor"
              duration="6:00"
            />
          </div>
          
          <Tip>
            Video tutorials are coming soon! Check back for visual guides on using all features of Station Logbook.
          </Tip>
        </Section>
        
        {/* Quick Start */}
        <Section title="Quick Start Guide" icon={CheckCircle} defaultOpen={true}>
          <p className="mb-4 text-zinc-600 dark:text-zinc-400">
            Get started with Station Logbook in just a few steps:
          </p>
          <Step number="1" title="Log In">
            Enter your Employee ID and password on the login page. If your organization uses SSO, 
            click "Sign in with GridGate SSO" to authenticate through your corporate identity provider.
          </Step>
          <Step number="2" title="View Dashboard">
            After logging in, you'll see the Dashboard with all available stations. Use the search 
            bar to quickly find a specific station.
          </Step>
          <Step number="3" title="Start a Station Visit">
            Click "New Entry" or select a station from the dashboard to begin logging your visit.
          </Step>
          <Step number="4" title="Complete Your Log">
            Fill in all required information about your station visit, including activities performed, 
            equipment checked, and any issues found.
          </Step>
          <Tip>
            Your work is automatically saved as you type. You can leave and return to complete 
            an entry later.
          </Tip>
        </Section>

        {/* Login & Authentication */}
        <Section title="Login & Authentication" icon={LogIn}>
          <h4 className="font-semibold mb-3">Standard Login</h4>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Use your assigned Employee ID and password to log in. Your Employee ID is typically 
            in the format: DEPT-XXX (e.g., ADMIN-001, WORKER-123).
          </p>
          
          <h4 className="font-semibold mb-3">Single Sign-On (SSO)</h4>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            If enabled, you can use GridGate SSO to log in with your corporate credentials. 
            Click the "Sign in with GridGate SSO" button to be redirected to your organization's 
            login page.
          </p>
          
          <h4 className="font-semibold mb-3">New User Registration</h4>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            New users can register by clicking the "Register" tab on the login page. You'll need 
            to provide your name, employee ID, password, role, and contact information.
          </p>
          
          <Warning>
            Never share your login credentials with others. Each user should have their own account 
            to ensure accurate activity tracking.
          </Warning>
        </Section>

        {/* Dashboard */}
        <Section title="Dashboard" icon={Home}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            The Dashboard is your home screen after logging in. It provides an overview of all 
            stations and quick access to common actions.
          </p>
          
          <h4 className="font-semibold mb-3">Features</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li><strong>Station List:</strong> View all available substations with their details</li>
            <li><strong>Search:</strong> Quickly find stations by name, ID, or location</li>
            <li><strong>Quick Actions:</strong> Start a new entry directly from any station card</li>
            <li><strong>Status Indicators:</strong> See which stations have active visits</li>
          </ul>
          
          <Tip>
            Use the search bar at the top to filter stations. You can search by station name, 
            station code, city, or region.
          </Tip>
        </Section>

        {/* Station Entry */}
        <Section title="Creating a Station Entry" icon={ClipboardList}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Recording station visits is the core function of Station Logbook. Follow these steps 
            to create a complete and accurate log entry.
          </p>
          
          <Step number="1" title="Select a Station">
            From the Dashboard or Entry page, select the station you're visiting. You can search 
            by station name, code, or location.
          </Step>
          
          <Step number="2" title="Record Arrival Time">
            The system automatically records your arrival time when you start the entry. You can 
            adjust this if needed.
          </Step>
          
          <Step number="3" title="Document Activities">
            Select all activities performed during your visit from the checklist. Add notes for 
            any unusual findings or work completed.
          </Step>
          
          <Step number="4" title="Log Equipment Status">
            Record the status of equipment checked, including meter readings, transformer 
            conditions, and any maintenance performed.
          </Step>
          
          <Step number="5" title="Complete the Entry">
            Review all information and click "Complete Visit" to finalize your entry. The 
            departure time will be automatically recorded.
          </Step>
          
          <Warning>
            Ensure all required fields are completed before submitting. Incomplete entries may 
            need supervisor review.
          </Warning>
        </Section>

        {/* Map View */}
        <Section title="Station Map View" icon={Map}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            The interactive map displays all stations geographically, making it easy to find 
            nearby stations and plan routes.
          </p>
          
          <h4 className="font-semibold mb-3">Map Features</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li><strong>Marker Clustering:</strong> Stations are grouped when zoomed out for better visibility</li>
            <li><strong>Heatmap View:</strong> Toggle heatmap to see station density in different areas</li>
            <li><strong>Region Filter:</strong> Filter stations by geographic region</li>
            <li><strong>Office Filter:</strong> Filter by reporting office</li>
            <li><strong>Search:</strong> Find specific stations directly on the map</li>
          </ul>
          
          <h4 className="font-semibold mb-3">View Modes</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li><strong>Markers:</strong> Shows individual station markers</li>
            <li><strong>Heatmap:</strong> Shows station density visualization</li>
            <li><strong>Both:</strong> Combines markers with heatmap overlay</li>
          </ul>
          
          <Tip>
            Click on any station marker to view its details and quickly start a new entry.
          </Tip>
        </Section>

        {/* History */}
        <Section title="Visit History" icon={Clock}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Access your complete history of station visits for reference, reporting, and auditing.
          </p>
          
          <h4 className="font-semibold mb-3">Viewing History</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li>Navigate to "History" from the main menu</li>
            <li>Use filters to narrow down by date range, station, or status</li>
            <li>Click on any entry to view full details</li>
            <li>Export history to PDF for reports</li>
          </ul>
          
          <h4 className="font-semibold mb-3">Filtering Options</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li><strong>Date Range:</strong> Select start and end dates</li>
            <li><strong>Station:</strong> Filter by specific station</li>
            <li><strong>Employee:</strong> View entries by specific worker (supervisors only)</li>
            <li><strong>Status:</strong> Filter by completed, in-progress, or flagged entries</li>
          </ul>
        </Section>

        {/* Supervisor Features */}
        <Section title="Supervisor Features" icon={Users}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Supervisors have additional capabilities to manage teams and review entries.
          </p>
          
          <h4 className="font-semibold mb-3">Supervisor Dashboard</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li><strong>Team Overview:</strong> See all active visits from your team members</li>
            <li><strong>Pending Reviews:</strong> Entries flagged for supervisor review</li>
            <li><strong>Statistics:</strong> View team performance metrics</li>
            <li><strong>Alerts:</strong> Notifications for overdue entries or issues</li>
          </ul>
          
          <h4 className="font-semibold mb-3">Review Process</h4>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            When reviewing an entry, you can approve it, request corrections, or add supervisor 
            notes. All review actions are logged for audit purposes.
          </p>
        </Section>

        {/* Admin Features */}
        <Section title="Administrator Features" icon={Settings}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Administrators have full access to system configuration and user management.
          </p>
          
          <h4 className="font-semibold mb-3">User Management</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li>Create and manage user accounts</li>
            <li>Assign roles (Worker, Supervisor, Admin)</li>
            <li>Reset passwords and manage access</li>
            <li>View user activity logs</li>
          </ul>
          
          <h4 className="font-semibold mb-3">Station Management</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li>Add, edit, or remove stations</li>
            <li>Update station details and coordinates</li>
            <li>Import stations from spreadsheets</li>
            <li>Manage reporting offices</li>
          </ul>
          
          <h4 className="font-semibold mb-3">Office Locations</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-600 dark:text-zinc-400 mb-4">
            <li>Configure reporting office locations</li>
            <li>Assign stations to offices</li>
            <li>Set office contact information</li>
          </ul>
        </Section>

        {/* User Roles */}
        <Section title="User Roles & Permissions" icon={Shield}>
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            Station Logbook uses three user roles with different permission levels:
          </p>
          
          <div className="space-y-4">
            <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
              <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">Worker</h4>
              <ul className="list-disc list-inside text-sm text-zinc-600 dark:text-zinc-400">
                <li>Create and edit own station entries</li>
                <li>View station information and map</li>
                <li>Access personal visit history</li>
              </ul>
            </div>
            
            <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
              <h4 className="font-semibold text-blue-600 dark:text-blue-400 mb-2">Supervisor</h4>
              <ul className="list-disc list-inside text-sm text-zinc-600 dark:text-zinc-400">
                <li>All Worker permissions</li>
                <li>View and review team entries</li>
                <li>Access team dashboard and statistics</li>
                <li>Approve or flag entries for review</li>
              </ul>
            </div>
            
            <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
              <h4 className="font-semibold text-orange-600 dark:text-orange-400 mb-2">Administrator</h4>
              <ul className="list-disc list-inside text-sm text-zinc-600 dark:text-zinc-400">
                <li>All Supervisor permissions</li>
                <li>Manage users and roles</li>
                <li>Configure stations and offices</li>
                <li>Access system settings and audit logs</li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Troubleshooting */}
        <Section title="Troubleshooting" icon={HelpCircle}>
          <h4 className="font-semibold mb-3">Common Issues</h4>
          
          <div className="space-y-4">
            <div className="border-l-4 border-orange-500 pl-4">
              <h5 className="font-medium">Can't log in?</h5>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Verify your Employee ID and password. If using SSO, ensure your corporate 
                account is active. Contact your administrator if issues persist.
              </p>
            </div>
            
            <div className="border-l-4 border-orange-500 pl-4">
              <h5 className="font-medium">Station not found?</h5>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Try searching by station code instead of name. Check with your administrator 
                if a new station needs to be added to the system.
              </p>
            </div>
            
            <div className="border-l-4 border-orange-500 pl-4">
              <h5 className="font-medium">Entry not saving?</h5>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Ensure you have a stable internet connection. Check that all required fields 
                are completed. Try refreshing the page and re-entering the data.
              </p>
            </div>
            
            <div className="border-l-4 border-orange-500 pl-4">
              <h5 className="font-medium">Map not loading?</h5>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                The map requires an internet connection and may take a moment to load with 
                many stations. Try refreshing the page or clearing your browser cache.
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
            <h4 className="font-semibold mb-2">Need More Help?</h4>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Contact your system administrator or supervisor for additional assistance. 
              For technical issues, please include your Employee ID and a description 
              of the problem when reporting.
            </p>
          </div>
        </Section>

        {/* Footer */}
        <div className="text-center py-8 border-t border-zinc-200 dark:border-zinc-700 mt-8">
          <p className="text-zinc-500 text-sm">
            Station Logbook v1.0 | Digital Substation Log Book
          </p>
          <p className="text-zinc-400 text-xs mt-2">
            For support, contact your system administrator
          </p>
        </div>
      </div>
    </div>
  );
}
